'use client';

/**
 * Workspace Manager Component
 * Modal for creating, editing, and deleting workspaces
 */

import { useState } from 'react';
import { useWorkspace } from '@/lib/archimate/WorkspaceContext';
import type { WorkspaceType } from '@/lib/archimate/workspace-types';

interface WorkspaceManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WorkspaceManager({ isOpen, onClose }: WorkspaceManagerProps) {
  const { workspaces, createNewWorkspace, deleteWorkspaceById } = useWorkspace();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'local' as WorkspaceType,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    try {
      await createNewWorkspace({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type,
      });

      // Reset form
      setFormData({ name: '', description: '', type: 'local' });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create workspace:', error);
      alert('Failed to create workspace. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This will permanently delete all diagrams and folders in this workspace.`)) {
      return;
    }

    try {
      await deleteWorkspaceById(id);
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      alert('Failed to delete workspace. Please try again.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-background rounded-lg shadow-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border-muted">
            <h2 className="text-xl font-bold text-foreground">Workspace Manager</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Create Workspace Section */}
            <div className="mb-6">
              {!showCreateForm ? (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground font-medium rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Workspace
                </button>
              ) : (
                <div className="bg-background/50 border border-border rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Create New Workspace</h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="My Architecture Workspace"
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Optional description for this workspace"
                        rows={3}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-accent resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Storage Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setFormData({ ...formData, type: 'local' })}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            formData.type === 'local'
                              ? 'border-accent bg-accent-muted'
                              : 'border-border hover:border-border/40'
                          }`}
                        >
                          <svg className="w-6 h-6 text-foreground mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <p className="text-sm font-medium text-foreground">Local</p>
                          <p className="text-xs text-foreground-tertiary mt-1">Stored in browser only</p>
                        </button>

                        <button
                          onClick={() => setFormData({ ...formData, type: 'remote' })}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            formData.type === 'remote'
                              ? 'border-accent bg-accent-muted'
                              : 'border-border hover:border-border/40'
                          }`}
                        >
                          <svg className="w-6 h-6 text-accent mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                          </svg>
                          <p className="text-sm font-medium text-foreground">Remote</p>
                          <p className="text-xs text-foreground-tertiary mt-1">Synced to cloud</p>
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={handleCreate}
                        disabled={!formData.name.trim() || isSubmitting}
                        className="flex-1 px-4 py-2 bg-accent hover:bg-accent-hover disabled:bg-cream/20 disabled:cursor-not-allowed text-accent-foreground font-medium rounded-lg transition-colors"
                      >
                        {isSubmitting ? 'Creating...' : 'Create'}
                      </button>
                      <button
                        onClick={() => {
                          setShowCreateForm(false);
                          setFormData({ name: '', description: '', type: 'local' });
                        }}
                        className="px-4 py-2 border border-border hover:bg-cream/10 text-foreground rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Workspace List */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">All Workspaces</h3>
              <div className="space-y-2">
                {workspaces.map(workspace => (
                  <div
                    key={workspace.id}
                    className="flex items-start gap-3 p-4 bg-background/50 border border-border rounded-lg"
                  >
                    <div className="flex-shrink-0 mt-1">
                      {workspace.type === 'local' ? (
                        <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                        </svg>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-foreground">{workspace.name}</h4>
                        {workspace.isDefault && (
                          <span className="text-xs px-2 py-0.5 bg-accent-muted text-accent rounded">
                            Default
                          </span>
                        )}
                      </div>
                      {workspace.description && (
                        <p className="text-sm text-foreground-secondary mb-2">{workspace.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-foreground-tertiary">
                        <span>{workspace.diagramCount} diagrams</span>
                        <span>{workspace.folderCount} folders</span>
                        <span className="capitalize">{workspace.type}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDelete(workspace.id, workspace.name)}
                      disabled={workspace.isDefault}
                      className="p-2 hover:bg-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors group"
                      title={workspace.isDefault ? 'Cannot delete default workspace' : 'Delete workspace'}
                    >
                      <svg className="w-5 h-5 text-foreground-secondary group-hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-border-muted">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border hover:bg-cream/10 text-foreground rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
